import { Flex, Typography, theme } from 'antd';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { useDerivedNetworkContext, useFilterContext } from '@/context';
import type { HourlyPlaysHour } from '@/types/content';
import { formatNumber, hasHlsEnabledShow } from '@/utils';
import {
  closedHourEnd,
  hoursOnLocalToday,
  localDayTicks,
  localTodayBounds,
} from './localTodayHours';
import LivePulseDot from './LivePulseDot';
import { ChartTooltipWrapper } from '@/components/charts/ChartTooltip';

const localHourLabel = (tick: number): string =>
  new Date(tick).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/** Local start and end of the closed hour, e.g. 09:00–10:00. */
const localHourRangeLabel = (hourStart: number): string =>
  `${localHourLabel(hourStart)}–${localHourLabel(closedHourEnd(hourStart))}`;

/** Domain is local midnight through the next midnight. Tick labels use the browser timezone. */
const localDayAxis = (now = new Date()) => {
  const { dayStart, dayEnd } = localTodayBounds(now);
  const xTicks = localDayTicks(now);
  const hourLabels = new Map(xTicks.map((tick) => [tick, localHourLabel(tick)]));

  return { dayStart, dayEnd, xTicks, hourLabels };
};

const LiveDownloadsChart = ({ data }: { data: HourlyPlaysHour[] }) => {
  const { token } = theme.useToken();
  const { selectedNetworks } = useDerivedNetworkContext();
  const { filters } = useFilterContext();
  const hasHlsInSelection = hasHlsEnabledShow(selectedNetworks, filters.selectedShows);
  const { dayStart, dayEnd, xTicks, hourLabels } = localDayAxis();
  const todaysHours = hoursOnLocalToday(data);

  const chartData = todaysHours.map((row) => ({
    hour: Date.parse(row.hour_utc),
    total: row.listens + row.views,
    listens: row.listens,
    views: row.views,
  }));

  return (
    <Flex
      className="live-downloads-chart"
      style={{
        flex: '1 1 400px',
        minWidth: 'min(400px, 100%)',
        height: 44 + token.fontSizeSM,
        overflow: 'visible',
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 2, left: 16 }}>
          <CartesianGrid vertical={false} stroke={token.purple3} />
          <XAxis
            dataKey="hour"
            type="number"
            domain={[dayStart, dayEnd]}
            ticks={xTicks}
            axisLine={false}
            tickLine={false}
            height={14}
            tickMargin={2}
            tick={{ fill: token.colorTextTertiary, fontSize: 10 }}
            tickFormatter={(value: number) => hourLabels.get(value) ?? ''}
          />
          <YAxis hide width={0} />
          <Tooltip
            isAnimationActive={true}
            animationDuration={300}
            content={({ active, payload }: TooltipProps<number, string>) =>
              active && payload?.[0]?.payload ? (
                <ChartTooltipWrapper>
                  <Flex vertical gap={token.marginXS}>
                    <Typography.Text
                      style={{ fontSize: token.fontSizeSM, color: token.colorTextLightSolid }}
                    >
                      {localHourRangeLabel(payload[0].payload.hour)}
                    </Typography.Text>
                    <Flex vertical>
                      <Typography.Text
                        style={{ color: token.colorTextLightSolid, paddingBottom: 0 }}
                      >
                        Audio downloads:{' '}
                        <strong>{formatNumber('en', payload[0].payload.listens)}</strong>
                      </Typography.Text>
                      {hasHlsInSelection && (
                        <Typography.Text style={{ color: token.colorTextLightSolid }}>
                          Video views:{' '}
                          <strong>{formatNumber('en', payload[0].payload.views)}</strong>
                        </Typography.Text>
                      )}
                    </Flex>
                  </Flex>
                </ChartTooltipWrapper>
              ) : null
            }
          />
          <Area
            type="linear"
            dataKey="total"
            stroke={token.colorPrimary}
            strokeWidth={1.75}
            fill={token.purple2}
            fillOpacity={0.6}
            isAnimationActive={false}
            dot={(props) => {
              const { cx, cy, index } = props;
              // Only the latest hour gets a pulse. Recharts calls this for every point;
              // skip the rest. An empty <g /> is required because returning null is invalid.
              if (index !== chartData.length - 1 || cx == null || cy == null) {
                return <g key={index} />;
              }
              const pulseSize = 7;
              const pulseBox = 28;
              return (
                <foreignObject
                  key={index}
                  x={Number(cx) - pulseBox / 2}
                  y={Number(cy) - pulseBox / 2}
                  width={pulseBox}
                  height={pulseBox}
                  overflow="visible"
                >
                  <Flex
                    align="center"
                    justify="center"
                    style={{
                      width: pulseBox,
                      height: pulseBox,
                      overflow: 'visible',
                      fontSize: 0,
                      lineHeight: 0,
                    }}
                  >
                    <LivePulseDot color={token.colorPrimary} size={pulseSize} />
                  </Flex>
                </foreignObject>
              );
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Flex>
  );
};

export default LiveDownloadsChart;
